import React from 'react';
import {Text} from '@/ui-kits';
import {TermsAndConditionsData} from '@/constants/defaults';

interface TermsContentProps {
  className?: string;
  titleClassName?: string;
  contentClassName?: string;
}

export const TermsContent: React.FC<TermsContentProps> = ({
  className = '',
  titleClassName = '',
  contentClassName = '',
}) => {
  const getBodyIndentStyle = (title: string): React.CSSProperties => {
    const match = title.match(/^(\d+)\./);
    if (!match) return {};
    const digits = match[1].length;
    // number digits + "." + space ≈ (digits + 2)ch
    return {paddingLeft: `${digits + 2}ch`};
  };

  const renderSection = (section: typeof TermsAndConditionsData.sections[number]) => {
    const bodyIndentStyle = getBodyIndentStyle(section.title);
    const titleParts = section.title.match(/^(\d+\.)\s*(.*)$/);

    return (
      <div key={section.title}>
        <Text
          variant="headingSemiBold"
          className={`!text-base sm:!text-lg md:!text-xl lg:!text-2xl text-deep_blue mb-2 sm:mb-3 ${titleClassName} ${
            titleParts ? 'flex items-start gap-2' : ''
          }`}
        >
          {titleParts ? (
            <>
              <span className="shrink-0">{titleParts[1]}</span>
              <span className="min-w-0 flex-1">{titleParts[2]}</span>
            </>
          ) : (
            section.title
          )}
        </Text>

        <div style={bodyIndentStyle}>
            {section.type === 'text' && (
              <Text
                variant="body"
              className={`text-deep_blue !font-FigtreeRegular !text-xs sm:!text-sm md:!text-base leading-relaxed ${contentClassName}`}
              >
                {section.content as string}
              </Text>
            )}
        
            {section.type === 'list' && (
              <div className="space-y-4">
                {Array.from((section.content as unknown) as readonly (string | {number: string; description: string})[]).map((item, idx) => {
                  if (typeof item === 'string') {
                    return (
                      <div key={idx} className={`text-deep_blue font-FigtreeRegular text-xs sm:text-sm md:text-base leading-normal ${contentClassName}`}>
                        {item}
                      </div>
                    );
                  }
                  const parts = item.description.split('\n');
                  return (
                    <div
                      key={idx}
                      className={`flex items-start gap-1.5 text-deep_blue font-FigtreeRegular text-xs sm:text-sm leading-normal ${contentClassName}`}
                    >
                      <span className="font-FigtreeSemiBold min-w-[34px] text-deep_blue shrink-0">{item.number}</span>
                      <span className="flex flex-col gap-2">
                        {parts.map((line, i) => {
                          // Check if this line is a heading (Patient Obligations or Specialist Obligations)
                          const isHeading = line.includes('Patient Obligations:') || line.includes('Specialist Obligations:');
                          return (
                            <span key={i} className={isHeading ? 'font-FigtreeSemiBold' : ''}>
                              {line}
                            </span>
                          );
                        })}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
        
            {section.type === 'intro-list' && (
              <div>
                <Text
                  variant="body"
                  className={`text-deep_blue !font-FigtreeRegular !text-xs sm:!text-sm md:!text-base leading-relaxed mb-2 ${contentClassName}`}
                >
                  {((section.content as unknown) as {intro: string; items: readonly (string | {number: string; term: string; description: string})[]}).intro}
                </Text>
                <div className="space-y-4">
                  {Array.from(((section.content as unknown) as {intro: string; items: readonly (string | {number: string; term: string; description: string})[]}).items).map((item, idx) => {
                    if (typeof item === 'string') {
                      const isAddress = item.startsWith('Address: ') && item.includes('\n');
                      const isEmail = item.startsWith('Email: ');
                      if (isAddress) {
                        const label = 'Address: ';
                        const rest = item.slice(label.length);
                        return (
                          <div key={idx} className={`text-deep_blue font-FigtreeRegular text-xs sm:text-sm leading-normal flex items-start gap-1 ${contentClassName}`}>
                            <span className="shrink-0 font-FigtreeSemiBold">{label}</span>
                            <span className="whitespace-pre-line">{rest}</span>
                          </div>
                        );
                      }
                      if (isEmail) {
                        const label = 'Email: ';
                        const rest = item.slice(label.length);
                        return (
                          <div key={idx} className={`text-deep_blue font-FigtreeRegular text-xs sm:text-sm leading-normal flex items-start gap-1 ${contentClassName}`}>
                            <span className="shrink-0 font-FigtreeSemiBold">{label}</span>
                            <span>{rest}</span>
                          </div>
                        );
                      }
                      return (
                        <div key={idx} className={`text-deep_blue font-FigtreeRegular text-xs sm:text-sm leading-normal whitespace-pre-line ${contentClassName}`}>
                          {item}
                        </div>
                      );
                    }
                    return (
                      <div
                        key={idx}
                        className={`flex items-start gap-1.5 text-deep_blue font-FigtreeRegular text-xs sm:text-sm md:text-base leading-normal ${contentClassName}`}
                      >
                        <span className="font-FigtreeSemiBold min-w-[34px] text-deep_blue">{item.number}</span>
                        <span className="text-deep_blue">
                          <span className="font-FigtreeBold">{item.term}</span> {item.description}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
        </div>
      </div>
    );
  };

  return (
    <div className={className}>
      <Text variant="heading" className="font-FigtreeBold text-deep_blue mb-2 !text-xl sm:!text-2xl md:!text-3xl lg:!text-4xl">
        Terms and Conditions
      </Text>
      <Text variant="free" className="text-deep_blue !font-FigtreeRegular mb-3 sm:mb-4 md:mb-6 !text-xs sm:!text-sm md:!text-base">
        Last Updated: {TermsAndConditionsData.lastUpdated}
      </Text>
      <hr className="w-full border-t-2 border-ui_input_border_dark/40 mb-4 sm:mb-6" />

      {/* Single Column Layout for Terms */}
      <div className="flex flex-col gap-3 sm:gap-4 md:gap-6 lg:gap-8">
        {TermsAndConditionsData.sections.map((section, index) => (
          <div key={index}>
            {renderSection(section)}
          </div>
        ))}
      </div>
    </div>
  );
};
